import { BaseTrackingService } from '~/app/tracking/base-tracking-service';
import {
	BasketNavigationClickEvent,
	BasketNavigationClickEventParams,
} from '~/app/tracking/events/basket/basket-navigation-click-event';
import { CallbackEvent, CallbackEventParams } from '~/app/tracking/events/callback-event';
import { ConfigurationStartAfterEvent } from '~/app/tracking/events/configuration/configuration-start-after-event';
import { ConfigurationStartBeforeEvent } from '~/app/tracking/events/configuration/configuration-start-before-event';
import { AcceptConflict, AcceptConflictParams } from '~/app/tracking/events/conflict-layer/accept-conflict-click-event';
import {
	ConflictAccordionClickEvent,
	ConflictAccordionClickEventParams,
} from '~/app/tracking/events/conflict-layer/conflict-accordion-click-event';
import {
	ConflictClickEventParams,
	ConflictSelectItem,
} from '~/app/tracking/events/conflict-layer/conflict-select-item-click-event';
import {
	ConflictViewChangeClose,
	ConflictViewChangeOpen,
	ConflictViewChangeParams,
} from '~/app/tracking/events/conflict-layer/conflict-view-change';
import {
	DeclineConflict,
	DeclineConflictParams,
} from '~/app/tracking/events/conflict-layer/decline-conflict-click-event';
import { ErrorEvent, ErrorEventParams } from '~/app/tracking/events/error-event';
import { IncentiveEventParams, IncentivesClickEvent } from '~/app/tracking/events/incentives/incentives-click-event';
import { NbaClickEvent, NbaClickEventParams } from '~/app/tracking/events/nba/nba-content-click-event';
import { PageLoadEvent } from '~/app/tracking/events/page-load-event';
import { PriceUpdate, PriceUpdateParams } from '~/app/tracking/events/price-update';
import {
	SelectionClickEvent,
	SelectionClickEventParams,
} from '~/app/tracking/events/selection-steps/selection-click-event';
import {
	SelectionImpressionEvent,
	SelectionImpressionEventParams,
} from '~/app/tracking/events/selection-steps/selection-impression-event';
import { StockCarClickEvent, StockCarClickEventParams } from '~/app/tracking/events/stock-car-click-event';
import { GalleryClickEvent, GalleryClickEventParams } from '~/app/tracking/events/summary-page/gallery-click-event';
import { SummaryClickEvent, SummaryClickEventParams } from '~/app/tracking/events/summary-page/summary-click-event';
import {
	SummaryImpressionEvent,
	SummaryImpressionEventParams,
} from '~/app/tracking/events/summary-page/summary-impression-event';
import { ViewChangeEvent, ViewChangeEventParams } from '~/app/tracking/events/view-change-event';
import {
	CarPart,
	ErrorType,
	EventAction,
	LayerType,
	NbaTargetTypes,
	Package,
	ProductData,
	RelatedProducts,
} from '~/app/tracking/feld-m-types';
import { createItem, ItemTrackingData } from '~/app/tracking/use-configured-vehicle';

function compareCarParts<T extends CarPart>(oldCarParts: T[], newCarParts: T[]) {
	const addedItems = newCarParts.filter(
		(newItem) => !oldCarParts.some((oldItem) => oldItem.attributes.prString7 === newItem.attributes.prString7),
	);
	const removedItems = oldCarParts.filter(
		(oldItem) => !newCarParts.some((newItem) => newItem.attributes.prString7 === oldItem.attributes.prString7),
	);
	return { addedItems, removedItems };
}

function findAddedAndRemovedItems(oldState: ProductData, newState: ProductData) {
	const oldCarParts = oldState.configuredVehicle[0].carParts;
	const oldPackages = oldState.configuredVehicle[0].packages;

	const newCarParts = newState.configuredVehicle[0].carParts;
	const newPackages = newState.configuredVehicle[0].packages;

	const carPartsDiff = compareCarParts(oldCarParts, newCarParts);
	const packagesDiff = compareCarParts(oldPackages, newPackages);

	return {
		addedItems: {
			carParts: carPartsDiff.addedItems,
			packages: packagesDiff.addedItems,
		},
		removedItems: {
			carParts: carPartsDiff.removedItems,
			packages: packagesDiff.removedItems,
		},
	};
}

const createRelatedProducts = (
	products: {
		item: ItemTrackingData;
		selected?: boolean;
		manualOrAutomatic?: string;
	}[],
) => {
	// creates relatedProducts for tracking, we need to determine if an item was added or removed and if its a carPart or a package

	const relatedProducts: RelatedProducts = {
		addedItems: {
			carParts: [],
			packages: [],
		},
		removedItems: {
			carParts: [],
			packages: [],
		},
		displayedItems: {
			packages: [],
			carParts: [],
		},
	};

	products.forEach((product) => {
		const mappedItem = createItem(product.item, product.manualOrAutomatic);
		const isPackageOrFeature = product.item.__typename === 'ConfiguredCarPackage' ? 'packages' : 'carParts';

		if (product.selected === undefined) {
			(relatedProducts.displayedItems?.[isPackageOrFeature] as (CarPart | Package)[]).push(mappedItem);
		} else {
			const addedOrRemoved = product.selected ? 'removedItems' : 'addedItems';
			(relatedProducts[addedOrRemoved][isPackageOrFeature] as (CarPart | Package)[]).push(mappedItem);
		}
	});

	return relatedProducts;
};

export class AppTrackingService extends BaseTrackingService {
	private currentProduct?: ProductData;

	private prevPage: string | null = null;

	private didTrack = {
		configurationStartBefore: false,
		configurationStartAfter: false,
	};

	private configstartedWith?: ProductData;

	isInitialized: boolean = this.isInitialized;

	setProductData(productData: ProductData) {
		this.currentProduct = productData;
	}

	pageLoad(pageName: string, destination: string) {
		if (!this.currentProduct || this.prevPage === pageName) {
			return;
		}
		this.pushProduct(this.currentProduct);
		this.updateComponent({
			page: {
				pageInfo: {
					destinationURL: destination,
				},
			},
		});
		this.track(new PageLoadEvent({ pageName }));

		this.prevPage = pageName;
	}

	nbaClickEvent(params: NbaClickEventParams) {
		params.nbaName = Object.values(NbaTargetTypes).includes(params.nbaName as NbaTargetTypes)
			? params.nbaName
			: NbaTargetTypes.GenericLink;

		this.track(new NbaClickEvent(params));
	}

	basketNavigationClick(params: BasketNavigationClickEventParams) {
		this.pushProduct(this.currentProduct);
		this.track(new BasketNavigationClickEvent(params));
	}

	incentivesClickEvent(params: IncentiveEventParams) {
		this.track(new IncentivesClickEvent(params));
		this.priceUpdate({ value: 'tactical selected' });
	}

	configurationStartBefore() {
		if (this.didTrack.configurationStartBefore) return;
		try {
			this.configstartedWith = this.currentProduct ? JSON.parse(JSON.stringify(this.currentProduct)) : null;
			this.track(new ConfigurationStartBeforeEvent());
			this.didTrack.configurationStartBefore = true;
		} catch (error) {
			console.log('An error happened in the configurationStartBefore tracking event:', error);
		}
	}

	configurationStartAfter() {
		if (this.didTrack.configurationStartAfter || !this.configstartedWith || !this.currentProduct) return;
		const { addedItems, removedItems } = findAddedAndRemovedItems(this.configstartedWith, this.currentProduct);
		this.pushProduct(this.currentProduct);
		this.configstartedWith = JSON.parse(JSON.stringify(this.currentProduct));

		this.track(
			new ConfigurationStartAfterEvent({
				addedItems,
				removedItems,
				eventName: 'configurator - start (state after change)',
			}),
		);
		this.didTrack.configurationStartAfter = true;
	}

	configurationChange() {
		if (!this.didTrack.configurationStartAfter || !this.configstartedWith || !this.currentProduct) return;

		const { addedItems, removedItems } = findAddedAndRemovedItems(this.configstartedWith, this.currentProduct);
		if (
			!addedItems.carParts.length &&
			!addedItems.packages.length &&
			!removedItems.carParts.length &&
			!removedItems.packages.length
		)
			return;

		this.pushProduct(this.currentProduct);
		this.configstartedWith = JSON.parse(JSON.stringify(this.currentProduct));

		this.track(
			new ConfigurationStartAfterEvent({
				addedItems,
				removedItems,
				eventName: 'configurator - change',
			}),
		);
	}

	selectionStepImpression(params: SelectionImpressionEventParams) {
		const callback = () => new SelectionImpressionEvent(params).serialize();
		this.registerImpressionTracking(params.node, callback);
	}

	trackSelectionClickEvent(params: SelectionClickEventParams) {
		const { item, selected } = params;

		if (item) {
			params.relatedProducts = createRelatedProducts([
				{ item: item as unknown as ItemTrackingData, selected, manualOrAutomatic: 'manual' },
			]);
		}

		this.track(new SelectionClickEvent(params));
	}

	trackConflictOpen(params: ConflictViewChangeParams) {
		const { data } = params;
		const conflictItems = data!.solutionGroups.flatMap((group) =>
			group.solutions
				.filter((item) => item.feature.status.selected)
				.map((item) => ({
					item: item.feature,
					selected: item.action === 'REMOVE',
					manualOrAutomatic: 'automatic',
				})),
		);

		const triggerItem = {
			item: data?.trigger as ItemTrackingData,
			selected: (data?.trigger.price?.value ?? 0) < 0,
			manualOrAutomatic: 'manual',
		};

		const relatedProducts = createRelatedProducts([triggerItem, ...conflictItems]);

		this.updateLayerComponent(LayerType.ConflictLayer, 'open');

		this.track(new ConflictViewChangeOpen({ ...params, relatedProducts }));
	}

	trackConflictItemSelectionEvent(params: ConflictClickEventParams) {
		const relatedProducts = createRelatedProducts([{ item: params.selectedItem, manualOrAutomatic: 'manual' }]);

		this.track(new ConflictSelectItem({ ...params, relatedProducts }));
	}

	trackAcceptConflictEvent(params: AcceptConflictParams) {
		const { data } = params;

		const conflictItems = data!.solutionGroups.flatMap((group) =>
			group.solutions
				.filter((item) => item.feature.status.selected)
				.map((item) => ({
					item: item.feature,
					selected: item.action === 'REMOVE',
					manualOrAutomatic: 'automatic',
				})),
		);
		const triggerItem = {
			item: data?.trigger as ItemTrackingData,
			selected: (data?.trigger.price?.value ?? 0) < 0,
			manualOrAutomatic: 'manual',
		};

		const relatedProducts = createRelatedProducts([...conflictItems, triggerItem]);

		this.track(new AcceptConflict({ ...params, relatedProducts }));

		this.updateLayerComponent(LayerType.ConflictLayer, 'close');
	}

	trackConflictAccordionEvent(params: ConflictAccordionClickEventParams) {
		this.track(new ConflictAccordionClickEvent(params));
	}

	trackDeclineConflict(params: DeclineConflictParams) {
		this.track(new DeclineConflict(params));
		this.updateLayerComponent(LayerType.ConflictLayer, 'close');
	}

	trackConflictClose() {
		this.track(new ConflictViewChangeClose());
	}

	summaryImpression(params: SummaryImpressionEventParams) {
		const callback = () => new SummaryImpressionEvent(params).serialize();
		this.registerImpressionTracking(params.node, callback);
	}

	galleryClickEvent(params: GalleryClickEventParams) {
		this.track(new GalleryClickEvent(params));
	}

	summaryClickEvent(params: SummaryClickEventParams) {
		this.track(new SummaryClickEvent(params));
	}

	stockCarClickEvent(params: StockCarClickEventParams) {
		this.track(new StockCarClickEvent(params));
	}

	viewChangeEvent(params: ViewChangeEventParams) {
		const { item } = params;
		if (item) {
			params.relatedProducts = createRelatedProducts([{ item: item as unknown as ItemTrackingData }]);
		}
		this.track(new ViewChangeEvent(params));
	}

	nonInteractionEvent({
		eventName,
		componentName,
		value,
	}: {
		eventName: string;
		componentName: string;
		value?: string;
	}) {
		this.track(new CallbackEvent({ eventAction: EventAction.NonInteraction, eventName, componentName, value }));
	}

	saveConfiguration(params: CallbackEventParams) {
		this.track(new CallbackEvent(params));
		this.track(new ViewChangeEvent({ eventName: 'generic' }));
	}

	priceUpdate(params: PriceUpdateParams) {
		this.track(new PriceUpdate(params));
		this.pushProduct(this.currentProduct);
	}

	pushProductEvent() {
		if (!this.currentProduct) return;
		this.pushProduct(this.currentProduct);
	}

	trackError({ name, userMessage }: ErrorEventParams) {
		const errorKey =
			Object.keys(ErrorType).find((key) => ErrorType[key as keyof typeof ErrorType] === name) || name;
		this.updateComponent({
			error: {
				name: errorKey,
				userMessage,
			},
		});
		this.track(new ErrorEvent({ name, userMessage }));
	}

	updateLayerComponent(layerName: LayerType, action?: 'open' | 'close') {
		// Update the layer array in dataLayer.component
		const currentComponent = this.getCurrentComponent() as { page?: { pageInfo?: { layerName?: string[] } } };
		const currentLayerName = currentComponent?.page?.pageInfo?.layerName || [];

		let updatedLayerName: string[];

		if (action === 'open') {
			updatedLayerName = [...currentLayerName, layerName];
		} else {
			const lastIndex = currentLayerName.lastIndexOf(layerName);
			if (lastIndex !== -1) {
				updatedLayerName = [...currentLayerName.slice(0, lastIndex), ...currentLayerName.slice(lastIndex + 1)];
			} else {
				updatedLayerName = currentLayerName;
			}
		}

		this.updateComponent({
			page: {
				pageInfo: {
					layerName: updatedLayerName,
				},
			},
		});
	}

	private pushProduct(product?: ProductData) {
		if (!product) return;
		if (!window?.digitalData) {
			window.digitalData = {};
		}
		if (!window?.digitalData.product || !Array.isArray(window?.digitalData.product)) {
			window.digitalData.product = [];
		}
		(window.digitalData.product as unknown[]).push(product);
	}
}

export const appTrackingService = new AppTrackingService();
